import { Box, VStack } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { InfoPill } from '@/App/tiles/Option/common/InfoPill.tsx';
import { Expiry } from '@/App/tiles/Option/fields/Expiry.tsx';
import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { AmountReference } from '@/App/tiles/Option/mainFields/AmountReference.tsx';
import { BidAsk } from '@/App/tiles/Option/mainFields/BidAsk.tsx';
import { CallPutType } from '@/App/tiles/Option/mainFields/CallPutType.tsx';
import { CommercialMargin } from '@/App/tiles/Option/mainFields/CommercialMargin.tsx';
import { CrossCurrency } from '@/App/tiles/Option/mainFields/CrossCurrency.tsx';
import { Delivery } from '@/App/tiles/Option/mainFields/Delivery.tsx';
import { DeltaHedge } from '@/App/tiles/Option/mainFields/DeltaHedge.tsx';
import { FixingReference1 } from '@/App/tiles/Option/mainFields/FixingReference1.tsx';
import { FixingReference2 } from '@/App/tiles/Option/mainFields/FixingReference2.tsx';
import { GreeksData } from '@/App/tiles/Option/mainFields/GreeksData.tsx';
import { HedgeDate } from '@/App/tiles/Option/mainFields/HedgeDate.tsx';
import { HedgeTypeField } from '@/App/tiles/Option/mainFields/HedgeTypeField.tsx';
import { MarketParameters } from '@/App/tiles/Option/mainFields/MarketParameters.tsx';
import { Notional } from '@/App/tiles/Option/mainFields/Notional.tsx';
import { OptionSpot } from '@/App/tiles/Option/mainFields/OptionSpot.tsx';
import { PremiumDate } from '@/App/tiles/Option/mainFields/PremiumDate.tsx';
import { PriceButton } from '@/App/tiles/Option/mainFields/PriceButton.tsx';
import { PriceType } from '@/App/tiles/Option/mainFields/PriceType.tsx';
import { SettlementFields } from '@/App/tiles/Option/mainFields/SettlementFields.tsx';
import { SpotRef } from '@/App/tiles/Option/mainFields/SpotRef.tsx';
import { Strike } from '@/App/tiles/Option/mainFields/Strike.tsx';
import { Triggers } from '@/App/tiles/Option/mainFields/Triggers';
import { Type } from '@/App/tiles/Option/mainFields/Type.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { OptionFooter } from '@/App/tiles/Option/OptionFooter.tsx';
import { OptionHeader } from '@/App/tiles/Option/OptionHeader.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { computeHedgeDisplayRules, type HedgeDisplayRules } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';
import type { IntlMessageId } from '@/types/intl';

export function OptionMainPanel({ tileId }: { tileId: string }) {
  const { hedgeDisplay, strategyPanelMode, optionType, expandGreekAndMarketPanels, settlementType } = useOptionSelector(
    (option) => {
      const hedgeDisplay: HedgeDisplayRules = computeHedgeDisplayRules(option);
      const strategyPanelMode = option.properties.uiState.strategyPanelMode;
      const expandGreekAndMarketPanels = option.properties.uiState.expandGreekAndMarketPanels ?? false;

      return {
        hedgeDisplay,
        strategyPanelMode,
        optionType: option.type,
        expandGreekAndMarketPanels,
        settlementType: option.properties.settlementType,
      };
    },
  );

  const [greeksPanelExpanded, setGreeksPanelExpanded] = useState(expandGreekAndMarketPanels);
  const [marketPanelExpanded, setMarketPanelExpanded] = useState(expandGreekAndMarketPanels);
  const [triggersExpanded, setTriggersExpanded] = useState(true);
  const [commercialMarginExpanded, setCommercialMarginExpanded] = useState(false);

  const {
    currentUser: { userType },
  } = useUserContext();
  const isInternalSales = userType === 'Internal Sales';
  const mainPanelClass = strategyPanelMode !== 'hidden' ? 'col-4' : 'col-12';

  return (
    <VStack verticalAlignment="start" style={{ maxWidth: 312 }} className={mainPanelClass}>
      <OptionHeader tileId={tileId} />
      <Box className="pb-8px px-8px w-100">
        {strategyPanelMode === 'hidden' && (
          <>
            <Type tileId={tileId} />
            {(optionType === 'Vanilla' || optionType === 'Trigger') && <CallPutType tileId={tileId} />}
            <OptionFormGroup label={<FormattedMessage id={'option.fields.expiry'} />}>
              <Expiry legId={`${tileId}/0`} />
            </OptionFormGroup>
            {optionType === 'RiskReversal' || optionType === 'Strangle' ? (
              <>
                <MainStrike legId={`${tileId}/0`} labelId="option.fields.callStrike" data-e2e="call-strike" />
                <MainStrike legId={`${tileId}/1`} labelId="option.fields.putStrike" data-e2e="put-strike" />
              </>
            ) : (
              optionType !== 'Binary' && (
                <MainStrike legId={`${tileId}/0`} labelId="option.fields.strike" data-e2e="strike" />
              )
            )}
            <Notional tileId={tileId} />
            <Delivery tileId={tileId} />
            <SettlementFields tileId={tileId} />
            <Triggers
              tileId={tileId}
              expanded={triggersExpanded}
              onClick={(collapseStatus) => {
                setTriggersExpanded(collapseStatus === 'expanded');
              }}
            />

            {settlementType === 'Cash' && (
              <div className="mt-3">
                <CrossCurrency />
                <FixingReference1 />
                <FixingReference2 />
              </div>
            )}
          </>
        )}
        <OptionSpot tileId={tileId} />
        {hedgeDisplay.hedgeRate === 'compact' && <SpotRef tileId={tileId} />}
      </Box>
      <div className="border-top border-bottom">
        <BidAsk tileId={tileId} />
      </div>
      <Box padding="8px">
        {strategyPanelMode !== 'hidden' && <SettlementFields tileId={tileId} />}
        <PriceType tileId={tileId} />
        <AmountReference tileId={tileId} />
        {hedgeDisplay.premiumDate === 'compact' && <PremiumDate tileId={tileId} />}
        <HedgeTypeField tileId={tileId} />

        {hedgeDisplay.hedgeDate === 'compact' && <HedgeDate tileId={tileId} />}
        {hedgeDisplay.hedgeAmount === 'compact' && <DeltaHedge tileId={tileId} />}

        <GreeksData
          tileId={tileId}
          canCollapse={strategyPanelMode === 'hidden'}
          expanded={greeksPanelExpanded}
          onClick={(collapseStatus) => {
            setGreeksPanelExpanded(collapseStatus === 'expanded');
          }}
        />

        {strategyPanelMode === 'hidden' && (
          <MarketParameters
            tileId={tileId}
            expanded={marketPanelExpanded}
            onClick={(collapseStatus) => {
              setMarketPanelExpanded(collapseStatus === 'expanded');
            }}
          />
        )}

        {strategyPanelMode !== 'hidden' && isInternalSales && (
          <CommercialMargin
            tileId={tileId}
            expanded={commercialMarginExpanded}
            onClick={(collapseStatus) => {
              setCommercialMarginExpanded(collapseStatus === 'expanded');
            }}
          />
        )}
      </Box>
      <div className="d-flex flex-column px-2 gap-2 mb-2">
        <PriceButton tileId={tileId} />
        <OptionFooter />
      </div>
    </VStack>
  );
}

function MainStrike(props: { labelId: IntlMessageId; 'data-e2e': string; legId: LegId }) {
  return (
    <OptionFormGroup
      label={<FormattedMessage id={props.labelId} />}
      addon={<InfoPill messageId="option.fields.strike.tooltip" />}
    >
      <Strike legId={props.legId} dataE2e={props['data-e2e']} />
    </OptionFormGroup>
  );
}
